/**
 * The complete analytics taxonomy.
 *
 * Rules for adding to this file:
 * - `snake_case`, `noun_verb-in-past-tense` (`event_booked`, not `book_event`).
 * - An event that is authoritative about money or state MUST be captured on
 *   the server (`captureServerEventSafely`). Client-reported revenue is lossy
 *   exactly when it matters — the user is bouncing back from a payment
 *   redirect and the WebView may never resume.
 * - Every `*_STARTED` needs a matching `*_SUCCEEDED` and `*_FAILED`, or the
 *   funnel cannot show you where users drop.
 *
 * `src/lib/analytics/events.test.ts` fails the build if an event declared here
 * is never fired, so this list stays honest about real coverage.
 */
export const ANALYTICS_EVENTS = {
  ACQUISITION_LANDING_VIEWED: "acquisition_landing_viewed",
  APP_STORE_LINK_CLICKED: "app_store_link_clicked",
  APP_FIRST_OPENED: "app_first_opened",
  NATIVE_AD_MEASUREMENT_UPDATED: "native_ad_measurement_updated",
  DEEP_LINK_OPENED: "deep_link_opened",
  ACQUISITION_IDENTITY_LINKED: "acquisition_identity_linked",
  AD_MEASUREMENT_CONSENT_CHANGED: "ad_measurement_consent_changed",
  AD_CONVERSION_DELIVERY_FAILED: "ad_conversion_delivery_failed",
  AD_CONVERSION_OUTBOX_HEALTH_RECORDED: "ad_conversion_outbox_health_recorded",

  ACCOUNT_DELETED: "account_deleted",
  ACCOUNT_PAUSED: "account_paused",
  ACCOUNT_REACTIVATED: "account_reactivated",
  ACTIVITY_POST_PUBLISHED: "activity_post_published",
  ACTIVITY_RECORDING_CANCELLED: "activity_recording_cancelled",
  ACTIVITY_RECORDING_PAUSED: "activity_recording_paused",
  ACTIVITY_RECORDING_RESUMED: "activity_recording_resumed",
  ACTIVITY_RECORDING_STARTED: "activity_recording_started",
  APP_ERROR: "app_error",
  /** A verified phone number was added to, or replaced on, an existing account. */
  AUTH_PHONE_CREDENTIAL_ATTACHED: "auth_phone_credential_attached",
  /** A shared browser moved directly from one signed-in account to another. */
  AUTH_ACCOUNT_SWITCHED: "auth_account_switched",
  AUTH_PROVIDER_SELECTED: "auth_provider_selected",
  AUTH_SESSION_IDENTIFIED: "auth_session_identified",
  AUTH_SIGNED_OUT: "auth_signed_out",
  BOTTOM_NAV_ITEM_CLICKED: "bottom-nav-item-clicked",
  BUDDY_REQUEST_ACCEPTED: "buddy_request_accepted",
  BUDDY_REQUEST_REJECTED: "buddy_request_rejected",
  BUDDY_REQUEST_SENT: "buddy_request_sent",
  BUDDY_REQUEST_CANCELLED: "buddy_request_cancelled",
  BUDDY_REQUESTS_VIEWED: "buddy_requests_viewed",
  BUDDY_REMOVED: "buddy_removed",
  CHAT_CREATED: "chat_created",
  CHAT_DISAPPEARING_TIMER_SET: "chat_disappearing_timer_set",
  CHAT_MESSAGE_FORWARDED: "chat_message_forwarded",
  CHAT_MESSAGE_PINNED: "chat_message_pinned",
  CHAT_MESSAGE_SENT: "chat_message_sent",
  CHAT_MESSAGE_SEARCHED: "chat_message_searched",
  CHAT_POLL_VOTED: "chat_poll_voted",
  CHAT_VIEW_ONCE_OPENED: "chat_view_once_opened",
  CHAT_REPLY_CANCELLED: "chat_reply_cancelled",
  CHAT_SHARE_SENT: "chat_share_sent",
  /**
   * The checkout asked "does this person need to sign in?", with the answer.
   *
   * `checkout_auth_prompted` only ever fired on the prompted branch, so the
   * members who sail straight through contributed nothing and the auth step
   * looked like a cliff instead of a fork. Both outcomes are on this one event
   * so the split is a single number, not two unrelated counts.
   */
  CHECKOUT_AUTH_GATE_EVALUATED: "checkout_auth_gate_evaluated",
  /** Sign-in offered from inside a booking flow, with the basket already saved. */
  CHECKOUT_AUTH_PROMPTED: "checkout_auth_prompted",
  /**
   * A session appeared while a checkout was waiting on one, with the provider
   * that produced it. `checkout_auth_resumed` is not a substitute: it fires
   * only when a stashed basket was replayed, so every single-tap sign-in and
   * every abandoned-then-resumed purchase was missing from the auth funnel.
   */
  CHECKOUT_AUTH_COMPLETED: "checkout_auth_completed",
  /** Came back from that sign-in and the saved basket was replayed. */
  CHECKOUT_AUTH_RESUMED: "checkout_auth_resumed",
  /**
   * A purchase was refused for want of a verified mobile number.
   *
   * The conversion cost of the payment gate, which was previously invisible:
   * the refusal happened server-side and emitted nothing, so the only symptom
   * was checkouts that stopped.
   */
  CHECKOUT_PHONE_GATE_BLOCKED: "checkout_phone_gate_blocked",
  /**
   * A booking flow stopped to ask for a profile fact it needed — today only
   * gender, on a gender-restricted event.
   *
   * The three events below are one funnel, and the useful number is the gap
   * between them: prompted-minus-(saved+refused) is people who closed the
   * question rather than answer it, which is invisible from the booking events
   * alone because no booking was ever attempted.
   */
  DEFERRED_PROFILE_ANSWER_PROMPTED: "deferred_profile_answer_prompted",
  /** They answered, and the answer was written to their profile. */
  DEFERRED_PROFILE_ANSWER_SAVED: "deferred_profile_answer_saved",
  /** They answered, and the answer disqualifies them from this event. */
  DEFERRED_PROFILE_ANSWER_REFUSED: "deferred_profile_answer_refused",
  COMMENT_CREATED: "comment_created",
  /** Someone tagged @hiivebot; one event per request, with outcome. */
  HIIVE_BOT_REQUEST: "hiive_bot_request",
  HIIVE_BOT_INFERENCE: "hiive_bot_inference",
  /** Someone submitted an email address for ticket delivery. */
  CONTACT_EMAIL_SUBMITTED: "contact_email_submitted",
  /** They declined the email ask. The honest half of the funnel. */
  CONTACT_EMAIL_SKIPPED: "contact_email_skipped",
  /** The code in that inbox was entered correctly; the address is now usable. */
  CONTACT_EMAIL_VERIFIED: "contact_email_verified",
  CONTACT_SYNC_COMPLETED: "contact_sync_completed",
  CONTACT_SYNC_PROMPT_VIEWED: "contact_sync_prompt_viewed",
  CONTACT_SYNC_SKIPPED: "contact_sync_skipped",
  /** The code in that inbox was wrong, expired, or out of attempts. */
  CONTACT_EMAIL_VERIFICATION_FAILED: "contact_email_verification_failed",
  /** A confirmed booking's ticket email entered the outbox. */
  BOOKING_TICKET_EMAIL_QUEUED: "booking_ticket_email_queued",
  /** SES accepted it. Delivery itself is reported by the webhook, not here. */
  BOOKING_TICKET_EMAIL_SENT: "booking_ticket_email_sent",
  BOOKING_TICKET_EMAIL_FAILED: "booking_ticket_email_failed",
  /**
   * Guest ticketing — booking a seat for somebody who is not on Hiive.
   *
   * The whole feature previously emitted nothing, so "how many bookings assign
   * seats to guests" and "do those guests ever claim an account" were both
   * unanswerable.
   */
  GUEST_ATTENDEE_ASSIGNED: "guest_attendee_assigned",
  /** A guest's email matched a verified account, so they got a real seat. */
  GUEST_ATTENDEE_LINKED_TO_ACCOUNT: "guest_attendee_linked_to_account",
  /** Somebody opened `/t/{id}` — usually from the emailed ticket. */
  GUEST_TICKET_VIEWED: "guest_ticket_viewed",
  /** Outcome of pressing "this is me" on a guest ticket. */
  GUEST_TICKET_CLAIM_ATTEMPTED: "guest_ticket_claim_attempted",
  /** A venue re-sent a guest their ticket from the dashboard. */
  GUEST_TICKET_RESENT: "guest_ticket_resent",
  EVENT_BOOK_TICKETS_CLICKED: "event_book_tickets_clicked",
  EVENT_BOOKED: "event_booked",
  EVENT_BOOKING_BLOCKED: "event_booking_blocked",
  EVENT_BOOKING_FAILED: "event_booking_failed",
  EVENT_BOOKING_FREE_CONFIRM_COMPLETED: "event_booking_free_confirm_completed",
  EVENT_BOOKING_FREE_CONFIRM_FAILED: "event_booking_free_confirm_failed",
  EVENT_BOOKING_FREE_CONFIRM_STARTED: "event_booking_free_confirm_started",
  EVENT_BOOKING_PASS_QUANTITY_ADJUSTED: "event_booking_pass_quantity_adjusted",
  EVENT_BOOKING_PAYMENT_ABANDON_CONFIRMED:
    "event_booking_payment_abandon_confirmed",
  EVENT_BOOKING_PAYMENT_ABANDON_DISMISSED:
    "event_booking_payment_abandon_dismissed",
  EVENT_BOOKING_PAYMENT_ABANDON_FAILED: "event_booking_payment_abandon_failed",
  EVENT_BOOKING_PAYMENT_ABANDON_REQUESTED:
    "event_booking_payment_abandon_requested",
  EVENT_BOOKING_PAYMENT_ABANDONED: "event_booking_payment_abandoned",
  EVENT_BOOKING_PAYMENT_ATTEMPT_CREATED:
    "event_booking_payment_attempt_created",
  EVENT_BOOKING_PAYMENT_PROVIDER_FAILED:
    "event_booking_payment_provider_failed",
  EVENT_BOOKING_PAYMENT_OUTCOME_PENDING:
    "event_booking_payment_outcome_pending",
  EVENT_BOOKING_PAYMENT_ATTEMPT_FAILED: "event_booking_payment_attempt_failed",
  /**
   * The handoff to a UPI app produced no outcome at all.
   *
   * Not a failure — a silence. Safari's "the address is invalid" alert is
   * invisible to JavaScript: the checkout stays open, the provider never emits
   * `payment.failed`, and the launch promise never settles, so the payer's
   * abandonment is the only thing the funnel sees. This event is what makes
   * that case countable, and `app_switch_observed` on it is what separates
   * "no app ever opened" from "opened the app and walked away".
   */
  EVENT_BOOKING_PAYMENT_HANDOFF_UNRESOLVED:
    "event_booking_payment_handoff_unresolved",
  EVENT_BOOKING_PAYMENT_LAUNCH_COALESCED:
    "event_booking_payment_launch_coalesced",
  EVENT_BOOKING_PAYMENT_COMPLETED: "event_booking_payment_completed",
  EVENT_BOOKING_PAYMENT_ERROR: "event_booking_payment_error",
  EVENT_BOOKING_PAYMENT_FAILED: "event_booking_payment_failed",
  EVENT_BOOKING_PAYMENT_METHOD_ADD_STARTED:
    "event_booking_payment_method_add_started",
  EVENT_BOOKING_PAYMENT_METHOD_SELECTED:
    "event_booking_payment_method_selected",
  EVENT_BOOKING_PAYMENT_METHOD_SELECTOR_OPENED:
    "event_booking_payment_method_selector_opened",
  EVENT_BOOKING_PAYMENT_METHOD_VALIDATION_FAILED:
    "event_booking_payment_method_validation_failed",
  EVENT_BOOKING_PAYMENT_REPAIR_CHECKED: "event_booking_payment_repair_checked",
  EVENT_BOOKING_PAYMENT_REPAIR_FAILED: "event_booking_payment_repair_failed",
  EVENT_BOOKING_PAYMENT_STARTED: "event_booking_payment_started",
  EVENT_BOOKING_PAYMENT_VALIDATION_FAILED:
    "event_booking_payment_validation_failed",
  EVENT_BOOKING_PAYMENT_VIEWED: "event_booking_payment_viewed",
  EVENT_BOOKING_STARTED: "event_booking_started",
  EVENT_BOOKING_SUBMITTED: "event_booking_submitted",
  EVENT_BOOKING_CHECKOUT_CHECKPOINT_RECORDED:
    "event_booking_checkout_checkpoint_recorded",
  EVENT_BOOKING_SUCCEEDED: "event_booking_succeeded",
  /**
   * A confirmed booking's own ticket was opened in the wallet.
   *
   * Only the guest path (`guest_ticket_viewed`) reported anything, so for
   * members the funnel ended at the payment and never showed whether the thing
   * they bought was ever looked at before the door.
   */
  EVENT_BOOKING_TICKET_VIEWED: "event_booking_ticket_viewed",
  EVENT_CREATED: "event_created",
  EVENT_CREATION_ABANDONED: "event_creation_abandoned",
  EVENT_CREATION_STARTED: "event_creation_started",
  EVENT_CREATION_STEP_COMPLETED: "event_creation_step_completed",
  EVENT_UPDATED: "event_updated",
  EVENT_LOCATION_CLICKED: "event_location_clicked",
  EVENT_SAVED: "event_saved",
  EVENT_SHARED: "event_shared",
  EVENT_TAB_CHANGED: "event_tab_changed",
  /**
   * The members-only detour, measured on the EVENT rather than the hiive.
   *
   * `tier_*` events already existed but carry only `hiive_slug`, and every
   * event-analytics query scopes on `event_slug` — so for a tier-gated event
   * the entire membership journey was invisible. Its queen saw arrivals, then
   * a cliff, with no way to tell "nobody wanted this" from "everybody hit the
   * tier wall and went off to ask for access".
   *
   * All four carry `event_slug` + `hiive_slug` so the existing scope reaches
   * them, and they are a BRANCH, never funnel spine: a member who already
   * holds the tier walks straight past every one of them.
   */
  EVENT_TIER_GATE_VIEWED: "event_tier_gate_viewed",
  EVENT_TIER_GATE_CTA_CLICKED: "event_tier_gate_cta_clicked",
  EVENT_TIER_JOIN_REQUESTED: "event_tier_join_requested",
  EVENT_TIER_ACCESS_GRANTED: "event_tier_access_granted",
  EVENT_VIEWED: "event_viewed",
  EVENT_WAITLIST_JOIN_ATTEMPTED: "event_waitlist_join_attempted",
  EVENT_WAITLIST_JOIN_FAILED: "event_waitlist_join_failed",
  EVENT_WAITLIST_JOIN_SUCCEEDED: "event_waitlist_join_succeeded",
  EVENT_WAITLIST_LEAVE_ATTEMPTED: "event_waitlist_leave_attempted",
  EVENT_WAITLIST_LEAVE_FAILED: "event_waitlist_leave_failed",
  EVENT_WAITLIST_LEAVE_SUCCEEDED: "event_waitlist_leave_succeeded",
  FEED_VIEWED: "feed_viewed",
  ACTIVITY_SESSION_CREATED: "activity_session_created",
  ACTIVITY_SESSION_JOINED: "activity_session_joined",
  GAME_INVITE_ACCEPTED: "game_invite_accepted",
  GAME_INVITE_SHARE_CLICKED: "game_invite_share_clicked",
  GLOBAL_APP_ERROR: "global_app_error",
  GLOBAL_SEARCH_OPENED: "global_search_opened",
  GLOBAL_SEARCH_QUERY_SUBMITTED: "global_search_query_submitted",
  HIIVE_CREATED: "hiive_created",
  HIIVE_CREATION_STARTED: "hiive_creation_started",
  HIIVE_CTA_CLICKED: "hiive_cta_clicked",
  HIIVE_INVITE_SENT: "hiive_invite_sent",
  HIIVE_JOIN_STARTED: "hiive_join_started",
  HIIVE_JOINED: "hiive_joined",
  HIIVE_LEFT: "hiive_left",
  HIIVE_SHARED: "hiive_shared",
  HIIVE_TAB_CHANGED: "hiive_tab_changed",
  HIIVE_VIEWED: "hiive_viewed",
  IMAGE_LOAD_FAILED: "image_load_failed",
  LOGIN_CLICKED: "login_clicked",
  MESSAGE_SENT: "message_sent",
  MOVEMENT_ACTIVITY_SAVE_FAILED: "movement_activity_save_failed",
  MOVEMENT_ACTIVITY_SAVE_IDEMPOTENT: "movement_activity_save_idempotent",
  MOVEMENT_ACTIVITY_SAVE_SIDE_EFFECT_FAILED:
    "movement_activity_save_side_effect_failed",
  MOVEMENT_ACTIVITY_SAVE_STARTED: "movement_activity_save_started",
  MOVEMENT_ACTIVITY_SAVE_SUCCEEDED: "movement_activity_save_succeeded",
  MOVEMENT_LIVE_LOCATION_PUBLISH_FAILED:
    "movement_live_location_publish_failed",
  MEDIA_CROP_CANCEL_CONFIRMED: "media_crop_cancel_confirmed",
  MEDIA_CROP_CANCEL_DISMISSED: "media_crop_cancel_dismissed",
  MEDIA_CROP_CANCEL_REQUESTED: "media_crop_cancel_requested",
  MEMBERSHIP_PAYMENT_STARTED: "membership_payment_started",
  MEMBERSHIP_PAYMENT_FAILED: "membership_payment_failed",
  MEMBERSHIP_PAYMENT_REPAIR_CHECKED: "membership_payment_repair_checked",
  MEMBERSHIP_PAYMENT_LAUNCH_COALESCED: "membership_payment_launch_coalesced",
  ONBOARDING_COMPLETED: "onboarding_completed",
  ONBOARDING_SKIPPED: "onboarding_skipped",
  ONBOARDING_STARTED: "onboarding_started",
  ONBOARDING_STEP_COMPLETED: "onboarding_step_completed",
  ONBOARDING_STEP_FAILED: "onboarding_step_failed",
  ONBOARDING_STEP_PREVIOUS_CLICKED: "onboarding_step_previous_clicked",
  ONBOARDING_STEP_VIEWED: "onboarding_step_viewed",
  PAYMENT_METHOD_ADDED: "payment_method_added",
  PAYMENT_METHOD_ADD_STARTED: "payment_method_add_started",
  PAYMENT_METHOD_REMOVED: "payment_method_removed",
  PAYMENT_METHOD_VALIDATION_FAILED: "payment_method_validation_failed",
  PAYMENT_METHOD_SELECTED: "payment_method_selected",
  PAYMENT_VALIDATION_FAILED: "payment_validation_failed",
  PAYMENT_ATTEMPT_CREATED: "payment_attempt_created",
  PAYMENT_PROVIDER_FAILED: "payment_provider_failed",
  PAYMENT_OUTCOME_PENDING: "payment_outcome_pending",
  PAYMENT_ATTEMPT_FAILED: "payment_attempt_failed",
  /** See EVENT_BOOKING_PAYMENT_HANDOFF_UNRESOLVED. */
  PAYMENT_HANDOFF_UNRESOLVED: "payment_handoff_unresolved",
  PAYMENT_LAUNCH_COALESCED: "payment_launch_coalesced",
  SHOP_PAYMENT_REPAIR_CHECKED: "shop_payment_repair_checked",
  VENUE_BOOKING_PAYMENT_REPAIR_CHECKED: "venue_booking_payment_repair_checked",
  WALLET_TOPUP_PAYMENT_REPAIR_CHECKED: "wallet_topup_payment_repair_checked",
  PHONE_VERIFICATION_COMPLETED: "phone_verification_completed",
  /**
   * Client-observed failure, tagged with `failure_code` (`OTP_EXPIRED`,
   * `OTP_INVALID`, `TOO_MANY_REQUESTS`, `SERVICE_UNAVAILABLE`, …) so the funnel
   * can split "code died" from "code mistyped". The server emits
   * `PHONE_VERIFICATION_REJECTED` with the refusal reason for its own
   * boundaries (rate limits, circuit, collisions).
   */
  PHONE_VERIFICATION_FAILED: "phone_verification_failed",
  /**
   * Server-side refusal, with the reason. Rate limits, an open provider
   * circuit and credential collisions used to produce log lines and nothing
   * else, so the OTP funnel could show a drop with no explanation for it.
   */
  PHONE_VERIFICATION_REJECTED: "phone_verification_rejected",
  PHONE_VERIFICATION_REQUESTED: "phone_verification_requested",
  POST_CREATED: "post_created",
  POST_LIKED: "post_liked",
  POST_SHARED: "post_shared",
  POST_VIEWED: "post_viewed",
  PROFILE_ACTIVITY_CLICKED: "profile-activity-clicked",
  PROFILE_CREATED: "profile_created",
  PROFILE_GOAL_BADGE_CLICKED: "profile-goal-badge-clicked",
  /** Music on profiles: a visitor (or the owner) started a song preview. */
  PROFILE_MUSIC_PREVIEW_PLAYED: "profile_music_preview_played",
  /** Music on profiles: songs saved; `songCount` 0 means removed. */
  PROFILE_MUSIC_UPDATED: "profile_music_updated",
  PROFILE_PICTURE_UPLOADED: "profile_picture_uploaded",
  PROFILE_UPDATED: "profile_updated",
  USERNAME_CHANGED: "username_changed",
  OPERATOR_CREATION_ERROR_FIX_SELECTED: "operator_creation_error_fix_selected",
  OPERATOR_CREATION_ERROR_PRESENTED: "operator_creation_error_presented",
  OPERATOR_CREATION_FAILED: "operator_creation_failed",
  PUSH_NOTIFICATION_PERMISSION_DENIED: "push_notification_permission_denied",
  PUSH_NOTIFICATION_PERMISSION_GRANTED: "push_notification_permission_granted",
  // Fired when the app ASKS the OS, not when the user sees anything. The old
  // name (PROMPT_VIEWED) described an impression it never measured, which made
  // the opt-in funnel unreadable — on a denied or provisional-exhausted device
  // no dialog appears at all, yet the event fired every time.
  PUSH_NOTIFICATION_PERMISSION_REQUESTED:
    "push_notification_permission_requested",
  // The nudge funnel proper. Every one carries `surface` and `variant`, which is
  // the only way to tell a good placement from a good variant.
  PUSH_NUDGE_SHOWN: "push_nudge_shown",
  PUSH_NUDGE_DISMISSED: "push_nudge_dismissed",
  PUSH_NUDGE_CTA_TAPPED: "push_nudge_cta_tapped",
  PUSH_NUDGE_PROMPT_FIRED: "push_nudge_prompt_fired",
  PUSH_NUDGE_GRANTED: "push_nudge_granted",
  PUSH_NUDGE_DENIED: "push_nudge_denied",
  // Onboarding's quiet grant. Separate from the nudge funnel: it measures
  // reach won without a dialog, not a nudge that converted.
  PUSH_NOTIFICATION_PROVISIONAL_GRANTED:
    "push_notification_provisional_granted",
  PUSH_NOTIFICATION_RECEIVED: "push_notification_received",
  PUSH_NOTIFICATION_TAPPED: "push_notification_tapped",
  PWA_INSTALL_PROMPT_VIEWED: "pwa_install_prompt_viewed",
  PWA_INSTALLED: "pwa_installed",
  SEARCH_RESULT_CLICKED: "search_result_clicked",
  SIGNUP_COMPLETED: "signup_completed",
  SIGNUP_STARTED: "signup_started",
  QUEEN_WORKSPACE_SELECTED: "queen_workspace_selected",
  QUEEN_WORKSPACE_SELECTION_REQUIRED: "queen_workspace_selection_required",
  REFERRAL_CODE_APPLIED: "referral_code_applied",
  REFERRAL_HUB_VIEWED: "referral_hub_viewed",
  REFERRAL_LINK_COPIED: "referral_link_copied",
  REFERRAL_REWARD_CLAIMED: "referral_reward_claimed",
  REFERRAL_REWARD_CLAIM_FAILED: "referral_reward_claim_failed",
  REFERRAL_SHARE_CLICKED: "referral_share_clicked",
  SOCIAL_SUGGESTION_COLD_START_BOOTSTRAP_FAILED:
    "social_suggestion_cold_start_bootstrap_failed",
  SOCIAL_SUGGESTION_COLD_START_BOOTSTRAP_TIMEOUT:
    "social_suggestion_cold_start_bootstrap_timeout",
  SOCIAL_SUGGESTION_CONVERSION: "social_suggestion_conversion",
  SOCIAL_SUGGESTION_INTERACTION: "social_suggestion_interaction",
  SOCIAL_SUGGESTION_REFRESH_COMPLETED: "social_suggestion_refresh_completed",
  SOCIAL_SUGGESTION_REFRESH_FAILED: "social_suggestion_refresh_failed",
  STORY_CONTRIBUTION_REVIEWED: "story_contribution_reviewed",
  STORY_CONTRIBUTION_SUBMITTED: "story_contribution_submitted",
  STORY_DELETED: "story_deleted",
  STORY_HIGHLIGHT_CREATED: "story_highlight_created",
  STORY_POSTED: "story_posted",
  STORY_RECAP_PUBLISHED: "story_recap_published",
  STORY_REMOVED_BY_ADMIN: "story_removed_by_admin",
  STORY_VIEWED: "story_viewed",
  TIER_JOIN_ATTEMPTED: "tier_join_attempted",
  TIER_JOIN_FAILED: "tier_join_failed",
  TIER_JOIN_REQUEST_CANCEL_ATTEMPTED: "tier_join_request_cancel_attempted",
  TIER_JOIN_REQUEST_CANCEL_FAILED: "tier_join_request_cancel_failed",
  TIER_JOIN_REQUEST_CANCEL_SUCCEEDED: "tier_join_request_cancel_succeeded",
  TIER_JOIN_REQUESTED: "tier_join_requested",
  TIER_JOIN_SUCCEEDED: "tier_join_succeeded",
  TIER_SELECTION_NAVIGATED_BACK: "tier_selection_navigated_back",
  TIER_VIEWED: "tier_viewed",
  USER_PROFILE_VIEWED: "user_profile_viewed",
  VENUE_BOOKING_FAILED: "venue_booking_failed",
  /**
   * Venue checkout spine, mirroring the event booking payment funnel.
   *
   * The venue payment page previously emitted only the shared `payment_*` set
   * (tagged `payment_flow: "venue_booking"`) plus a repair probe. That set has no
   * "reached the screen" or "money landed" event at all, so venue bookings were
   * invisible past the `venue_booking_succeeded` ack and any venue payment funnel
   * had to stop at a step that fires 200ms after submission. These close that gap.
   *
   * Confirmed bookings stay server-side via `venue_booking_confirmed` — the
   * client must never be the source of a revenue number.
   */
  VENUE_BOOKING_PAYMENT_ABANDONED: "venue_booking_payment_abandoned",
  VENUE_BOOKING_PAYMENT_COMPLETED: "venue_booking_payment_completed",
  VENUE_BOOKING_PAYMENT_FAILED: "venue_booking_payment_failed",
  VENUE_BOOKING_PAYMENT_STARTED: "venue_booking_payment_started",
  VENUE_BOOKING_PAYMENT_VIEWED: "venue_booking_payment_viewed",
  VENUE_BOOKING_SLOT_SELECTED: "venue_booking_slot_selected",
  VENUE_BOOKING_STARTED: "venue_booking_started",
  VENUE_BOOKING_SUBMITTED: "venue_booking_submitted",
  VENUE_BOOKING_SUCCEEDED: "venue_booking_succeeded",
  VENUE_EXPERIENCE_VIEWED: "venue_experience_viewed",
  VENUE_SHARED: "venue_shared",
  VENUE_VIEWED: "venue_viewed",
  WALLET_TOPUP_COMPLETED: "wallet_topup_completed",
  WALLET_TOPUP_STARTED: "wallet_topup_started",
  WALLET_VIEWED: "wallet_viewed",
  WEIGHT_TRAINING_SET_LOGGED: "weight_training_set_logged",
  WEIGHT_TRAINING_WORKOUT_COMPLETED: "weight_training_workout_completed",
  WEIGHT_TRAINING_WORKOUT_STARTED: "weight_training_workout_started",

  // ---------------------------------------------------------------------
  // Reliability & app health
  // ---------------------------------------------------------------------
  /** Unhandled rejection / uncaught error / failed resource load. */
  CLIENT_UNHANDLED_ERROR: "client_unhandled_error",
  /**
   * A first-party Next.js chunk failed to load. Recorded as an event, not an
   * exception: the stale deployment it signals is repaired by a reload, so an
   * error-tracking issue would only ever describe a self-healed page load.
   */
  CLIENT_ASSET_LOAD_FAILED: "client_asset_load_failed",
  /** A route-group boundary caught a render error (in-shell, recoverable). */
  SECTION_ERROR: "section_error",
  /** Core Web Vitals as an event so perf is chartable and alertable. */
  WEB_VITAL_MEASURED: "web_vital_measured",
  /** A server action returned `ok: false` or threw. */
  SERVER_ACTION_FAILED: "server_action_failed",
  APP_FOREGROUNDED: "app_foregrounded",
  APP_BACKGROUNDED: "app_backgrounded",
  APP_OFFLINE_DETECTED: "app_offline_detected",
  APP_ONLINE_RESTORED: "app_online_restored",
  APP_UPDATE_APPLIED: "app_update_applied",
  REALTIME_CONNECTED: "realtime_connected",
  REALTIME_DISCONNECTED: "realtime_disconnected",
  REALTIME_FALLBACK_ENGAGED: "realtime_fallback_engaged",
  // A route has been sitting on its loading skeleton long past any plausible
  // render time. Emitted before recovery so the stuck URL is observable even
  // when the reload succeeds and the user never reports it.
  NAVIGATION_STUCK_DETECTED: "navigation_stuck_detected",
  NAVIGATION_STUCK_RECOVERED: "navigation_stuck_recovered",
  MEDIA_UPLOAD_STARTED: "media_upload_started",
  MEDIA_UPLOAD_SUCCEEDED: "media_upload_succeeded",
  MEDIA_UPLOAD_FAILED: "media_upload_failed",
  VIDEO_PLAYBACK_STARTED: "video_playback_started",
  VIDEO_PLAYBACK_FAILED: "video_playback_failed",
  /** The autoplay provider had to restart (or mute) the chosen video. */
  VIDEO_AUTOPLAY_RECOVERED: "video_autoplay_recovered",
  MEDIA_PROCESSING_SUCCEEDED: "media_processing_succeeded",
  MEDIA_PROCESSING_FAILED: "media_processing_failed",
  MEDIA_REPAIR_COMPLETED: "media_repair_completed",
  PUSH_NOTIFICATION_REGISTRATION_FAILED:
    "push_notification_registration_failed",
  PUSH_NOTIFICATION_TOKEN_REGISTERED: "push_notification_token_registered",

  // ---------------------------------------------------------------------
  // Money — server-authoritative unless noted
  // ---------------------------------------------------------------------
  PAYMENT_CAPTURED: "payment_captured",
  PAYMENT_CAPTURE_FAILED: "payment_capture_failed",
  PAYMENT_REFUND_ISSUED: "payment_refund_issued",
  PAYMENT_REFUND_FAILED: "payment_refund_failed",
  PAYMENT_CHARGEBACK_RECORDED: "payment_chargeback_recorded",
  CREDIT_GRANTED: "credit_granted",
  CREDIT_REDEEMED: "credit_redeemed",
  CREDIT_EXPIRED: "credit_expired",
  WALLET_TOPUP_FAILED: "wallet_topup_failed",
  PAYOUT_BATCH_EXECUTED: "payout_batch_executed",
  PAYOUT_BATCH_FAILED: "payout_batch_failed",
  PAYOUT_PAYABLE_CREATED: "payout_payable_created",
  PROMO_CODE_APPLIED: "promo_code_applied",
  PROMO_CODE_REJECTED: "promo_code_rejected",
  REFUND_REQUESTED: "refund_requested",

  // ---------------------------------------------------------------------
  // Bookings & tickets — server-authoritative
  // ---------------------------------------------------------------------
  EVENT_BOOKING_CONFIRMED: "event_booking_confirmed",
  EVENT_BOOKING_CANCELLED: "event_booking_cancelled",
  EVENT_BOOKING_EXPIRED: "event_booking_expired",
  VENUE_BOOKING_CONFIRMED: "venue_booking_confirmed",
  VENUE_BOOKING_CANCELLED: "venue_booking_cancelled",
  /**
   * A held venue booking timed out before payment. Mirrors
   * `event_booking_expired`: the reservation unwound with no money moving, so
   * it must not read as a booking or as a cancellation with a refund.
   */
  VENUE_BOOKING_EXPIRED: "venue_booking_expired",
  VENUE_BOOKING_RESCHEDULED: "venue_booking_rescheduled",
  EVENT_WALK_IN_RECORDED: "event_walk_in_recorded",
  EVENT_TICKET_SCANNED: "event_ticket_scanned",
  EVENT_TICKET_SCAN_REJECTED: "event_ticket_scan_rejected",

  // ---------------------------------------------------------------------
  // Chat
  // ---------------------------------------------------------------------
  CHAT_LIST_VIEWED: "chat_list_viewed",
  CHAT_OPENED: "chat_opened",
  CHAT_MESSAGE_SEND_FAILED: "chat_message_send_failed",
  CHAT_MESSAGE_DELETED: "chat_message_deleted",
  CHAT_MESSAGE_REACTION_ADDED: "chat_message_reaction_added",
  CHAT_MESSAGE_REPLIED: "chat_message_replied",
  CHAT_MEDIA_SENT: "chat_media_sent",
  CHAT_VOICE_NOTE_SENT: "chat_voice_note_sent",
  CHAT_GIF_SENT: "chat_gif_sent",

  // ---------------------------------------------------------------------
  // Stories & instants
  // ---------------------------------------------------------------------
  STORY_COMPOSER_OPENED: "story_composer_opened",
  STORY_PUBLISH_FAILED: "story_publish_failed",
  STORY_REPLY_SENT: "story_reply_sent",
  STORY_REACTION_SENT: "story_reaction_sent",
  STORY_SHARE_CLICKED: "story_share_clicked",
  INSTANT_CAPTURE_STARTED: "instant_capture_started",
  INSTANT_SENT: "instant_sent",
  INSTANT_OPENED: "instant_opened",
  INSTANT_EXPIRED_VIEW_ATTEMPTED: "instant_expired_view_attempted",

  // ---------------------------------------------------------------------
  // Feed, discovery & search
  // ---------------------------------------------------------------------
  FEED_REFRESHED: "feed_refreshed",
  FEED_TAB_CHANGED: "feed_tab_changed",
  DISCOVER_VIEWED: "discover_viewed",
  DISCOVERY_IMPRESSION: "discovery_impression",
  DISCOVER_FILTER_APPLIED: "discover_filter_applied",
  SEARCH_NO_RESULTS: "search_no_results",

  // ---------------------------------------------------------------------
  // Profile & safety
  // ---------------------------------------------------------------------
  PROFILE_TAB_CHANGED: "profile_tab_changed",
  PROFILE_SHARED: "profile_shared",
  USER_BLOCKED: "user_blocked",
  USER_UNBLOCKED: "user_unblocked",
  USER_REPORTED: "user_reported",
  CONTENT_REPORTED: "content_reported",

  // ---------------------------------------------------------------------
  // Notifications
  // ---------------------------------------------------------------------
  NOTIFICATION_CENTER_VIEWED: "notification_center_viewed",
  NOTIFICATION_CLICKED: "notification_clicked",

  // ---------------------------------------------------------------------
  // Shop
  // ---------------------------------------------------------------------
  SHOP_VIEWED: "shop_viewed",
  SHOP_PRODUCT_VIEWED: "shop_product_viewed",
  SHOP_CHECKOUT_STARTED: "shop_checkout_started",
  SHOP_ORDER_PLACED: "shop_order_placed",
  SHOP_ORDER_FAILED: "shop_order_failed",
  PACK_PURCHASE_FREE_CONFIRMED: "pack_purchase_free_confirmed",
  PACK_PURCHASE_STARTED: "pack_purchase_started",
  PACK_VIEWED: "pack_viewed",

  // ---------------------------------------------------------------------
  // Movement & weight training
  // ---------------------------------------------------------------------
  MOVEMENT_LOCATION_PERMISSION_GRANTED: "movement_location_permission_granted",
  MOVEMENT_LOCATION_PERMISSION_DENIED: "movement_location_permission_denied",
  MOVEMENT_LIVE_SESSION_STARTED: "movement_live_session_started",
  MOVEMENT_LIVE_SESSION_ENDED: "movement_live_session_ended",
  WEIGHT_TRAINING_ROUTINE_CREATED: "weight_training_routine_created",
  WEIGHT_TRAINING_WORKOUT_ABANDONED: "weight_training_workout_abandoned",
  WEIGHT_TRAINING_BODY_METRIC_LOGGED: "weight_training_body_metric_logged",

  // ---------------------------------------------------------------------
  // Sessions & splits
  // ---------------------------------------------------------------------
  ACTIVITY_SESSION_LEFT: "activity_session_left",
  ACTIVITY_SESSION_SPLIT_CREATED: "activity_session_split_created",
  ACTIVITY_SESSION_SPLIT_SETTLED: "activity_session_split_settled",

  // ---------------------------------------------------------------------
  // Operator surfaces
  // ---------------------------------------------------------------------
  QUEEN_SECTION_VIEWED: "queen_section_viewed",
  ADMIN_ACTION_PERFORMED: "admin_action_performed",
} as const;

export type AnalyticsEventName =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export type AnalyticsProperties = Record<string, unknown>;
